import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import CountUp from 'react-countup';
import { fadeUp, stagger } from '@/lib/motion';
import { industries, ceoCockpitData, extraKpis, generateTxData, generateIncidentsData } from '@/lib/industryData';
import IndustrySelector, { getIndustryIcon } from '@/components/shared/IndustrySelector';
import { ArrowRight, MessageCircle, Wifi, TrendingUp, AlertTriangle, Clock, Download, Activity, Zap, ShieldCheck } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const kpiIcons = [Wifi, Activity, TrendingUp, AlertTriangle, Zap, ShieldCheck];

export default function CEOCockpitPage() {
  const [industry, setIndustry] = useState('retail');
  const [range, setRange] = useState('30d');
  const [kpiRef, kpiInView] = useInView({ triggerOnce: false, threshold: 0.2 });
  const [animKey, setAnimKey] = useState(0);

  const ind = industries.find(i => i.id === industry) || industries[0];
  const data = ceoCockpitData[industry] || ceoCockpitData.retail;
  const txData = generateTxData(industry);
  const incData = generateIncidentsData(industry);

  useEffect(() => {
    document.title = 'CEO Cockpit™ | ALLCOM — Operación y resultados en tiempo real';
  }, []);

  const switchIndustry = (id) => {
    setIndustry(id);
    setAnimKey(prev => prev + 1);
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Hero */}
      <div className="bg-[#050E1F] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <span className="inline-flex items-center gap-2 px-3 py-1 mb-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-amber-400 bg-amber-400/10 border border-amber-400/20 rounded-full">DEMO · Datos simulados</span>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight" style={{ fontFamily: 'Syne, sans-serif' }}>CEO Cockpit<sup className="text-xs align-super ml-0.5 opacity-60">™</sup></h1>
              <p className="text-slate-400 mt-1 text-sm sm:text-base">Una pantalla para ver tu operación: dispositivos, ventas, merma y alertas.</p>
            </div>
            <div className="flex gap-2">
              <Link to="/contacto" className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-[#0EA5E9] rounded-xl hover:bg-sky-600 shadow-lg" data-testid="ceocockpit-cta-demo">Agendar demo real <ArrowRight className="w-4 h-4" /></Link>
              <a href="https://wa.me/56975294340?text=Hola,%20quiero%20demo%20CEO%20Cockpit" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-emerald-400 border border-emerald-400/30 rounded-xl hover:bg-emerald-400/10"><MessageCircle className="w-4 h-4" /></a>
            </div>
          </div>
        </div>
      </div>

      {/* Industry Selector */}
      <IndustrySelector selected={industry} onSelect={switchIndustry} />

      {/* Toolbar */}
      <div className="bg-white border-b border-slate-100 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex flex-wrap items-center gap-3">
          <Link to="/" className="text-sm font-semibold text-[#0A2540] hover:text-[#0EA5E9]" style={{ fontFamily: 'Syne' }}>ALLCOM</Link>
          <span className="text-slate-300">/</span>
          <span className="text-sm text-slate-500">CEO Cockpit™</span>
          <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: `${ind.accent}15`, color: ind.accent }}>{ind.label}</span>
          <div className="ml-auto flex gap-2">
            {['7d', '30d', '90d'].map(r => (
              <button key={r} onClick={() => setRange(r)} className={`px-3 py-1.5 text-xs font-medium rounded-lg ${range === r ? 'bg-[#0A2540] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>{r}</button>
            ))}
            <button className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-slate-200 rounded-lg hover:bg-slate-50"><Download className="w-3.5 h-3.5" />Exportar</button>
          </div>
        </div>
      </div>

      {/* Dashboard */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" key={animKey}>
        {/* KPI Cards */}
        <motion.div ref={kpiRef} initial="hidden" animate="visible" variants={stagger} className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-8" data-testid="ceocockpit-kpis">
          {data.kpis.map((kpi, i) => {
            const Icon = kpiIcons[i] || Activity;
            return (
              <motion.div key={`${industry}-${i}`} variants={fadeUp} className="p-4 rounded-xl bg-white border border-slate-100 shadow-sm">
                <div className="flex items-center gap-2 mb-2">
                  <Icon className="w-4 h-4" style={{ color: ind.accent }} />
                  <span className="text-[11px] text-slate-500 font-medium">{kpi.label}</span>
                </div>
                <div className="text-2xl font-bold text-[#0A2540]" style={{ fontFamily: 'Syne' }}>
                  {kpi.prefix || ''}<CountUp key={`${industry}-${i}`} end={kpi.value} decimals={String(kpi.value).includes('.') ? 1 : 0} duration={1.5} /><span className="text-base" style={{ color: ind.accent }}>{kpi.suffix}</span>
                </div>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Charts Row 1 */}
        <div className="grid lg:grid-cols-3 gap-6 mb-6">
          <div className="lg:col-span-2 p-5 rounded-xl bg-white border border-slate-100 shadow-sm">
            <h3 className="text-sm font-semibold mb-4">Transacciones por día ({range})</h3>
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={txData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="day" tick={{ fontSize: 10 }} stroke="#94A3B8" />
                <YAxis tick={{ fontSize: 10 }} stroke="#94A3B8" />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                <Line type="monotone" dataKey="tx" stroke={ind.accent} strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="p-5 rounded-xl bg-white border border-slate-100 shadow-sm">
            <h3 className="text-sm font-semibold mb-4">Estado de flota</h3>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={data.fleet} cx="50%" cy="50%" innerRadius={55} outerRadius={80} paddingAngle={3} dataKey="value">
                  {data.fleet.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                </Pie>
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex justify-center gap-4 mt-2">
              {data.fleet.map((f, i) => (
                <div key={i} className="flex items-center gap-1.5 text-xs"><span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: f.color }} />{f.name} ({f.value})</div>
              ))}
            </div>
          </div>
        </div>

        {/* Charts Row 2 */}
        <div className="grid lg:grid-cols-3 gap-6 mb-6">
          <div className="p-5 rounded-xl bg-white border border-slate-100 shadow-sm">
            <h3 className="text-sm font-semibold mb-4">Incidentes por región</h3>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={incData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="region" tick={{ fontSize: 10 }} stroke="#94A3B8" />
                <YAxis tick={{ fontSize: 10 }} stroke="#94A3B8" />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                <Bar dataKey="incidents" fill={ind.accent} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="lg:col-span-2 p-5 rounded-xl bg-white border border-slate-100 shadow-sm">
            <h3 className="text-sm font-semibold mb-4">Top puntos con riesgo — {ind.icon} {ind.label}</h3>
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-slate-100">
                  <th className="text-left py-2 text-xs font-medium text-slate-400">Local</th>
                  <th className="text-left py-2 text-xs font-medium text-slate-400">Estado</th>
                  <th className="text-left py-2 text-xs font-medium text-slate-400">Merma</th>
                  <th className="text-left py-2 text-xs font-medium text-slate-400">Inc.</th>
                  <th className="text-left py-2 text-xs font-medium text-slate-400">Acción</th>
                </tr></thead>
                <tbody>{data.riskPoints.map((r, i) => (
                  <tr key={i} className="border-b border-slate-50">
                    <td className="py-2.5 text-xs font-medium">{r.local}</td>
                    <td className="py-2.5"><span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${r.estado === 'Online' ? 'bg-emerald-100 text-emerald-700' : r.estado === 'Degradado' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>{r.estado}</span></td>
                    <td className="py-2.5 text-xs">{r.merma}</td>
                    <td className="py-2.5 text-xs font-semibold">{r.incidentes}</td>
                    <td className="py-2.5 text-xs text-slate-500">{r.accion}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
            <div className="sm:hidden space-y-2">
              {data.riskPoints.map((r, i) => (
                <div key={i} className="p-3 rounded-lg bg-slate-50 border border-slate-100">
                  <div className="flex items-center justify-between mb-1"><span className="text-xs font-semibold">{r.local}</span><span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${r.estado === 'Online' ? 'bg-emerald-100 text-emerald-700' : r.estado === 'Degradado' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>{r.estado}</span></div>
                  <div className="text-[10px] text-slate-500">Merma: {r.merma} · Inc: {r.incidentes}</div>
                  <div className="text-[10px] mt-1" style={{ color: ind.accent }}>{r.accion}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Insights */}
        {data.insights && data.insights.length > 0 && (
          <div className="p-5 rounded-xl bg-white border border-slate-100 shadow-sm mb-8">
            <div className="flex items-center gap-2 mb-4">
              <Zap className="w-4 h-4" style={{ color: ind.accent }} />
              <h3 className="text-sm font-semibold">Insights — {ind.icon} {ind.label}</h3>
              <span className="text-[10px] px-2 py-0.5 bg-slate-100 text-slate-500 rounded-full">demo</span>
            </div>
            <div className="grid sm:grid-cols-3 gap-3">
              {data.insights.map((ins, i) => (
                <div key={i} className={`p-4 rounded-xl border ${ins.type === 'alert' ? 'bg-red-50 border-red-100' : ins.type === 'suggestion' ? 'bg-amber-50 border-amber-100' : 'bg-emerald-50 border-emerald-100'}`}>
                  <div className="text-[10px] font-semibold uppercase tracking-wider mb-1.5" style={{ color: ins.type === 'alert' ? '#EF4444' : ins.type === 'suggestion' ? '#F59E0B' : '#10B981' }}>
                    {ins.type === 'alert' ? 'Alerta' : ins.type === 'suggestion' ? 'Sugerencia' : 'Oportunidad'}
                  </div>
                  <p className="text-xs text-slate-700 leading-relaxed">{ins.text}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Extra KPIs - Industry Specific */}
        <div className="mb-8">
          <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4">Métricas adicionales — {ind.label}</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {(extraKpis[industry] || extraKpis.retail).map((kpi, i) => (
              <div key={`${industry}-extra-${i}`} className="p-4 rounded-xl bg-white border border-slate-100 shadow-sm text-center">
                <div className="text-lg font-bold text-[#0A2540]" style={{ fontFamily: 'Syne' }}>{kpi.value}</div>
                <div className="text-[10px] text-slate-400 font-medium mt-1">{kpi.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Minimal footer CTA */}
        <div className="text-center py-6 border-t border-slate-100">
          <p className="text-xs text-slate-400 mb-3">¿Quieres ver esto con datos reales de tu {ind.label.toLowerCase()}?</p>
          <div className="flex gap-3 justify-center">
            <Link to="/contacto" className="text-xs font-semibold px-4 py-2 rounded-lg text-white" style={{ backgroundColor: ind.accent }}>Agendar demo</Link>
            <a href="https://wa.me/56975294340" target="_blank" rel="noopener noreferrer" className="text-xs font-medium px-4 py-2 rounded-lg text-slate-500 border border-slate-200 hover:bg-slate-50">WhatsApp</a>
          </div>
        </div>
      </div>
    </div>
  );
}
